/**
 * stepsGroupState.js
 *
 * When the collapsible Workflow steps group has to open itself (#146).
 *
 * The group starts collapsed so the panel leads with the title, the execution
 * method and a short steps summary — her ask was "a very clean title, execution
 * method and steps". But two things must never be hidden inside it: a run
 * waiting on a human, and a step that failed. A checkpoint nobody can see is a
 * campaign that stalls until the reviewer thinks to expand a menu.
 *
 * Modelled on `activeExpansionSignal` in WorkflowCampaignPanel: a *signal
 * string* rather than a boolean, so the caller can force the group open when the
 * signal changes identity and still leave a deliberate collapse alone while the
 * same condition persists. Repeated identical events must not fight the user.
 *
 * Pure and dependency-free so it can be tested without rendering WorkflowPane.
 */

/** Execution statuses that mean a step did not succeed. */
const FAILED_STATUSES = new Set(['FAILED', 'ERROR', 'failed', 'error']);

function firstFailedStepIndex(executionStates = {}) {
    const entries = Object.entries(executionStates || {});
    for (let i = 0; i < entries.length; i += 1) {
        const [index, entry] = entries[i];
        const status = entry && typeof entry.status === 'string' ? entry.status : '';
        if (FAILED_STATUSES.has(status)) return index;
    }
    return null;
}

function firstInvalidStepIndex(stepValidation = {}) {
    const entries = Object.entries(stepValidation || {});
    for (let i = 0; i < entries.length; i += 1) {
        const [index, status] = entries[i];
        if (status === 'INVALID') return index;
    }
    return null;
}

/**
 * Why the steps group should be open right now, or null to leave it alone.
 *
 * The identity of the returned string is what matters: a caller opens the group
 * when it changes, so a new checkpoint or a newly failed step re-opens a group
 * the user collapsed, while an unchanged condition does not.
 *
 * @param {object} [input]
 * @param {object|null} [input.escalation] - live campaign checkpoint, if any
 * @param {object} [input.executionStates] - `{ [stepIndex]: { status } }`
 * @param {object} [input.stepValidation] - `{ [stepIndex]: StepStatus }`
 * @param {Array} [input.results] - per-step execution results
 * @returns {string|null}
 */
export function stepsExpansionSignal({
    escalation = null,
    executionStates = {},
    stepValidation = {},
    results = [],
} = {}) {
    // A human is being asked something. Highest priority: this one blocks a run.
    if (escalation) {
        const id = escalation.escalationId || escalation.id || 'active';
        return `checkpoint:${id}`;
    }
    // A step that failed. Keyed by which step, so a second failure re-opens.
    const failed = firstFailedStepIndex(executionStates);
    if (failed !== null) return `failed-step:${failed}`;
    // A result that reports failure even where the execution state does not —
    // the two are written by different paths and a run can report one without
    // the other.
    const failedResultIndex = (Array.isArray(results) ? results : [])
        .findIndex((entry) => entry && entry.success === false);
    if (failedResultIndex >= 0) return `failed-result:${failedResultIndex}`;
    // A step the reviewer cannot submit. Not a failure, but editing it is the
    // only way forward, and the editor lives inside this group.
    const invalid = firstInvalidStepIndex(stepValidation);
    if (invalid !== null) return `invalid-step:${invalid}`;
    return null;
}

/**
 * Should the group be open, given the previous signal and the user's choice?
 *
 * Kept next to the signal so the "a new reason wins, a repeated one does not"
 * rule has one home rather than being re-derived in the component.
 *
 * @param {object} [input]
 * @param {string|null} [input.signal] - current signal
 * @param {string|null} [input.previousSignal] - signal at the last evaluation
 * @param {boolean} [input.expanded] - the group's current state
 * @returns {boolean} the group's next state
 */
export function nextStepsExpanded({
    signal = null,
    previousSignal = null,
    expanded = false,
} = {}) {
    if (signal && signal !== previousSignal) return true;
    return expanded;
}
